import React from "react";
import { Grid, Typography, CardHeader, CardContent, Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartCard } from "../Style/health-chart.style";
import AgeWiseGenderChart from "./AgeWiseGenderChart";

export default function CancerTabCharts({
    selectedDistrict,
    allDistrictsChartData,
    currentTabLabel,
    ageWiseScreeningData,
}) {
    // Prepare oral cancer gender data
    const oralCancerData = selectedDistrict === 'ALL'
        ? allDistrictsChartData.map(d => ({
            district: d.DISTRICT,
            female: Number(d.sex_f_oral_cancer || 0),
            male: Number(d.sex_m_oral_cancer || 0)
        }))
        : [{
            district: selectedDistrict,
            female: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_f_oral_cancer || 0),
            male: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.sex_m_oral_cancer || 0)
        }];

    // Prepare breast/cervical cancer data
    const cancerData = selectedDistrict === 'ALL'
        ? allDistrictsChartData.map(d => ({
            district: d.DISTRICT,
            breast: Number(d.breast_cancer || 0),
            cervical: Number(d.cervical_cancer || 0)
        }))
        : [{
            district: selectedDistrict,
            breast: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.breast_cancer || 0),
            cervical: Number(allDistrictsChartData.find(d => d.DISTRICT === selectedDistrict)?.cervical_cancer || 0)
        }];

    // Sort oral cancer data by total count (low to high)
    const sortedOralCancerData = oralCancerData.sort((a, b) =>
        (a.female + a.male) - (b.female + b.male)
    );

    // Sort cancer data by total count (low to high)
    const sortedCancerData = cancerData.sort((a, b) =>
        (a.breast + a.cervical) - (b.breast + b.cervical)
    );

        const diseaseKeyMap = {
        'post stroke': 'STROKE',
        'mental health': 'Mental',
        'dm with htn': 'DMHTN',
        'bmi>=25': 'BMI',
        'cancer': 'CANCER',
        // Add other mappings if needed
    };

       const normalizedDistrict = selectedDistrict === 'ALL' ? 'ALL DISTRICTS' : selectedDistrict;

    // Get the data key, using the mapped value if it exists
    const dataKey = diseaseKeyMap[currentTabLabel?.toLowerCase()] || currentTabLabel;

    // Get the data for the current district and tab
    const districtData = ageWiseScreeningData?.[normalizedDistrict];
    const diseaseData = districtData?.[dataKey];

    return (
        <>
            {/* Age Wise Gender Chart - Full width at the top */}
            <Grid item xs={12} sx={{ mb: 0, width: '25%' }}>
                <Box sx={{ width: '100%', maxWidth: 1000, height: 564, margin: '0 auto' }}>
                    <AgeWiseGenderChart
                        ageWiseData={diseaseData}
                        selectedDistrict={selectedDistrict}
                        tabValue={9} // Cancer tab
                        diseaseName={currentTabLabel || 'Screening'}
                    />
                </Box>
            </Grid>

            <Grid item xs={12} md={6} sx={{ width: "500px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                Gender Wise Oral Cancer
                            </Typography>
                        }
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        <BarChart
                            xAxis={[{ scaleType: "linear" }]}
                            yAxis={[
                                {
                                    width: 100,
                                    scaleType: "band",
                                    data: sortedOralCancerData.map(item => item.district),
                                    tickLabelStyle: { fontSize: 12 },
                                },
                            ]}
                            series={[
                                {
                                    data: sortedOralCancerData.map(item => item.female),
                                    label: "Female",
                                    color: "#ff69b4",
                                },
                                {
                                    data: sortedOralCancerData.map(item => item.male),
                                    label: "Male",
                                    color: "#2196f3",
                                },
                            ]}
                            layout="horizontal"
                            height={455}
                            slotProps={{
                                legend: {
                                    direction: "horizontal",
                                    position: { horizontal: "center", vertical: "bottom" },
                                },
                            }}
                        />
                    </CardContent>
                </ChartCard>
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "500px" }}>
                <ChartCard>
                    <CardHeader
                        title={
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "bold", fontSize: "14px", textAlign: 'center', textDecorationLine: 'underline' }}
                            >
                                District Wise Breast/Cervical Cancer
                            </Typography>
                        }
                        sx={{ pb: 0 }}
                    />
                    <CardContent>
                        <BarChart
                            xAxis={[{ scaleType: "linear" }]}
                            yAxis={[
                                {
                                    width: 100,
                                    scaleType: "band",
                                    data: sortedCancerData.map(item => item.district),
                                    tickLabelStyle: { fontSize: 12 },
                                },
                            ]}
                            series={[
                                {
                                    data: sortedCancerData.map(item => item.breast),
                                    label: "Breast Cancer",
                                    color: "#e91e63",
                                },
                                {
                                    data: sortedCancerData.map(item => item.cervical),
                                    label: "Cervical Cancer",
                                    color: "#9c27b0",
                                },
                            ]}
                            layout="horizontal"
                            height={455}
                            slotProps={{
                                legend: {
                                    direction: "horizontal",
                                    position: { horizontal: "center", vertical: "bottom" },
                                },
                            }}
                        />
                    </CardContent>
                </ChartCard>
            </Grid>
        </>
    );
}
